import React, { useState } from 'react';
import { MapPin, Mail, Clock, Send, CheckCircle, ExternalLink } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    productType: '',
    quantity: '',
    message: ''
  });
  const [honeypot, setHoneypot] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [mapOpen, setMapOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch('https://formsubmit.co/ajax/orders@adelamerchants.com.au', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          Name: formData.name,
          Company: formData.company,
          Email: formData.email,
          Phone: formData.phone,
          'Product Type': formData.productType,
          'Estimated Quantity': formData.quantity,
          'Project Details': formData.message,
          _subject: `Website Quote Request — ${formData.name}`,
          _replyto: formData.email,
          _template: 'table',
          _captcha: 'false',
          _honey: honeypot
        })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || String(result.success) !== 'true') {
        throw new Error(result.message || `Request failed (${response.status})`);
      }
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          productType: '',
          quantity: '',
          message: ''
        });
      }, 5000);
    } catch (err) {
      console.error('Enquiry submission failed:', err);
      setSubmitError(
        'Sorry, your request could not be sent. Please try again, or email us directly at orders@adelamerchants.com.au.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const googleMapsUrl = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3385.7!2d115.9333!3d-32.0167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDAxJzAwLjEiUyAxMTXCsDU1JzU5LjkiRQ!5e0!3m2!1sen!2sau!4v1234567890`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=-32.0167,115.9333&destination_place_id=`;

  return (
    <section id="contact" className="py-24 bg-neutral-900 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-400/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-amber-400/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">Contact Us</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Request a Quote
          </h2>
          <p className="mt-6 text-lg text-neutral-400 max-w-2xl mx-auto">
            Get in touch with Perth's trusted paper core manufacturer. 
            We'll respond to your inquiry within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Cards */}
            <div className="space-y-4">
              {/* Email */}
              <div className="flex items-start gap-4 p-6 bg-neutral-950 border border-neutral-800 rounded-2xl hover:border-amber-400/30 transition-colors">
                <div className="w-12 h-12 bg-amber-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Email Us</h4>
                  <a 
                    href={`mailto:${companyInfo.email}`}
                    className="text-amber-400 hover:text-amber-300 transition-colors"
                  >
                    {companyInfo.email}
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-4 p-6 bg-neutral-950 border border-neutral-800 rounded-2xl hover:border-amber-400/30 transition-colors">
                <div className="w-12 h-12 bg-amber-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Visit Us</h4>
                  <p className="text-neutral-400">{companyInfo.address}</p>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start gap-4 p-6 bg-neutral-950 border border-neutral-800 rounded-2xl hover:border-amber-400/30 transition-colors">
                <div className="w-12 h-12 bg-amber-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Business Hours</h4>
                  <p className="text-neutral-400">{companyInfo.businessHours}</p>
                  <p className="text-amber-400 text-sm mt-1">{companyInfo.walkIns}</p>
                </div>
              </div>
            </div>

            {/* Map Preview */}
            <div 
              className="relative h-64 bg-neutral-950 border border-neutral-800 rounded-2xl overflow-hidden cursor-pointer group"
              onClick={() => setMapOpen(true)}
            >
              <iframe
                src={googleMapsUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Adela Merchants Location"
                className="grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-neutral-950/50 group-hover:bg-transparent transition-colors">
                <span className="px-4 py-2 bg-amber-400 text-neutral-900 font-semibold rounded-lg flex items-center gap-2 group-hover:scale-105 transition-transform">
                  <ExternalLink className="w-4 h-4" />
                  Click to Expand Map
                </span>
              </div>
            </div>
          </div>

          {/* Quote Request Form */}
          <div className="bg-neutral-950 border border-neutral-800 rounded-2xl p-8">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Quote Request Sent!</h3>
                <p className="text-neutral-400">We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* FormSubmit honeypot — real users never see or fill this */}
                <input
                  type="text"
                  name="_honey"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-neutral-300">Your Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-neutral-900 border-neutral-700 text-white focus:border-amber-400"
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-neutral-300">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-neutral-900 border-neutral-700 text-white focus:border-amber-400"
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-neutral-300">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-neutral-900 border-neutral-700 text-white focus:border-amber-400"
                      placeholder="john@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-neutral-300">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-neutral-900 border-neutral-700 text-white focus:border-amber-400"
                      placeholder="04XX XXX XXX"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="productType" className="text-neutral-300">Product Type *</Label>
                    <select
                      id="productType"
                      name="productType"
                      value={formData.productType}
                      onChange={handleChange}
                      required
                      className="w-full h-10 px-3 bg-neutral-900 border border-neutral-700 text-white rounded-md focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                    >
                      <option value="">Select product...</option>
                      <option value="paper-cores">Paper Cores</option>
                      <option value="cardboard-tubes">Cardboard Tubes</option>
                      <option value="printing-cores">Printing Cores</option>
                      <option value="concrete-tubes">Concrete Form Tubes</option>
                      <option value="packing-paper">Packing Paper</option>
                      <option value="label-stocks">Self Adhesive Label Stocks</option>
                      <option value="slitting-blades">Slitting Blades</option>
                      <option value="custom">Custom Order</option>
                      <option value="print-supplies">Print Supplies</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quantity" className="text-neutral-300">Estimated Quantity</Label>
                    <Input
                      id="quantity"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      className="bg-neutral-900 border-neutral-700 text-white focus:border-amber-400"
                      placeholder="e.g., 500 units"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-neutral-300">Project Details *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="bg-neutral-900 border-neutral-700 text-white focus:border-amber-400 resize-none"
                    placeholder="Please describe your requirements including sizes, specifications, and delivery timeline..."
                  />
                </div>

                {submitError && (
                  <p className="text-red-400 text-sm text-center" role="alert">
                    {submitError}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-amber-400 hover:bg-amber-300 text-neutral-900 font-semibold py-6 disabled:opacity-60"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Sending...' : 'Send Quote Request'}
                </Button>

                <p className="text-neutral-500 text-xs text-center">
                  We typically respond within 24 business hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Map Dialog */}
      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent className="max-w-4xl w-[90vw] h-[80vh] bg-neutral-950 border-neutral-800 p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-neutral-800">
            <DialogTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-400" />
              Adela Merchants - Cannington, Perth
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full relative">
            <iframe
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3385.7!2d115.9333!3d-32.0167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDAxJzAwLjEiUyAxMTXCsDU1JzU5LjkiRQ!5e0!3m2!1sen!2sau!4v1234567890`}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '500px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Adela Merchants Location - Full Map"
            />
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-4 right-4 px-6 py-3 bg-amber-400 text-neutral-900 font-semibold rounded-lg hover:bg-amber-300 transition-colors flex items-center gap-2 shadow-lg"
            >
              <ExternalLink className="w-4 h-4" />
              Get Directions
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ContactSection;
